import { Button, Typography } from 'omakase';
import { Card, CardActionArea, CardActionAreaProps, CardContent, CardProps } from 'ui';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Anchor from 'components/Anchor';
import React from 'react';
import clsx from 'clsx';

interface ActionCardProps {
  headline?: string;
  description?: string;
  icon?: React.ReactNode;
  actionText: string;
  href: string;
  className?: string;
  CardProps?: CardProps;
  CardActionAreaProps?: CardActionAreaProps;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardRoot: {
      borderRadius: 0,
      borderWidth: 0,
      overflow: 'visible',
    },
    contentDescription: {
      [theme.breakpoints.up('md')]: {
        padding: '2.4rem !important',
      },
    },
    contentCallToAction: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: 0,
    },
    actionText: {
      paddingLeft: '1.25rem',
    },
    contentHeadline: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1.5),
    },
    headline: {
      marginLeft: theme.spacing(1.5),
    },
    cardActionAreaRoot: {
      flex: 1,
      padding: 0,
      border: `1px solid ${theme.colors.borderDefault}`,
      borderBottomWidth: 2,
      position: 'relative',
      '&, &::after': {
        transition: `${theme.transitions.duration.short}ms border ${theme.transitions.easing.easeInOut}`,
      },
      // This is the very important full bleed border bottom.
      // It "removes" the weird native bevel corners you get with > 1px borders.
      '&::after': {
        borderBottom: `2px solid ${theme.colors.borderDefault}`,
        bottom: -2,
        content: '""',
        left: -1,
        position: 'absolute',
        width: 'calc(100% + 2px)',
      },
      '&:hover': {
        backgroundColor: theme.colors.bgDefault,
        textDecoration: 'none !important',
        '&::after': {
          borderBottomColor: theme.colors.borderPrimaryBase,
        },
      },
      '& .hoverableText': {
        transition: `${theme.transitions.duration.short}ms color ${theme.transitions.easing.easeInOut}`,
      },
      '&:hover .hoverableText': {
        color: theme.colors.fgPrimaryBase,
        '@media (hover: none)': {
          color: theme.colors.fgDefault,
        },
      },
      '&:focus:not(.Mui-focusVisible)': {
        boxShadow: 'none',
      },
      '&.Mui-focusVisible': {
        boxShadow: `0 0 0 1px ${theme.colors.borderPrimaryBase}`,
        '&, &::after': {
          borderColor: theme.colors.borderPrimaryBase,
        },
        '& .MuiCardActionArea-focusHighlight': {
          opacity: 0,
        },
      },
      '&:focus .hoverableText': {
        color: theme.colors.fgPrimaryBase,
      },
      '&:focus .MuiCardActionArea-focusHighlight': {
        boxShadow: 'none',
      },
      '&:active .hoverableText': {
        color: theme.colors.fgPrimaryBase,
      },
      '&:hover .MuiCardActionArea-focusHighlight': {
        opacity: 0,
      },
    },
  })
);

const ActionCard: React.FunctionComponent<ActionCardProps> = ({
  headline,
  description,
  icon,
  actionText,
  href,
  className,
  CardProps,
  CardActionAreaProps,
}) => {
  const classes = useStyles();
  return (
    <Card
      {...CardProps}
      variant="outlined"
      classes={{ root: classes.cardRoot }}
      className={className}
    >
      <CardActionArea
        {...CardActionAreaProps}
        classes={{
          root: classes.cardActionAreaRoot,
        }}
        component={Anchor}
        href={href}
        role="link"
        disableRipple
      >
        <CardContent className={classes.contentDescription}>
          <div className={classes.contentHeadline}>
            {icon}
            <Typography className={classes.headline} component="h4" variant="body01Strong">
              {headline}
            </Typography>
          </div>
          <Typography variant="body02" component="p">
            {description}
          </Typography>
        </CardContent>
        <CardContent className={clsx('hoverableText', classes.contentCallToAction)}>
          <Button
            tabIndex={-1}
            role="presentation"
            aria-label={`${actionText} (opens a dialog)`}
            className={classes.actionText}
            variant="basic"
            size="medium"
          >
            {actionText}
          </Button>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default ActionCard;
